/**
 * "Where did my money go?" tile — appears on the client portal home when the
 * `spend_insights` add-on is enabled. Backed by GET /api/spend-insights.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, ArrowRight, TrendingUp, TrendingDown, AlertTriangle, Loader2 } from "lucide-react";

import { api } from "@/lib/api";

const NZD = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 });

// Deterministic hue per category name so the little dot colours stay stable
// across renders — no random flash on refresh.
function hueFor(name) {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = ((h * 31) + name.charCodeAt(i)) & 0xffff;
    return h % 360;
}

export default function SpendInsightsTile() {
    const [data, setData] = useState(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        api.get("/spend-insights")
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoaded(true));
    }, []);

    if (!loaded) {
        return (
            <div className="co-card co-card-pad text-sm text-[#5E636E]" data-testid="spend-insights-tile-loading">
                <Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading spend breakdown…
            </div>
        );
    }
    if (!data) return null;
    if (!data.has_data) {
        return (
            <div className="co-card co-card-pad" data-testid="spend-insights-tile-empty">
                <div className="flex items-center gap-2 text-[#5E636E]">
                    <Wallet className="h-4 w-4" />
                    <span className="text-sm">We&apos;ll show your cash-out breakdown here once your accounting data is up to date.</span>
                </div>
            </div>
        );
    }

    const rows = [...(data.categories || [])];
    if (data.other) rows.push({ ...data.other, __isOther: true });
    const revenue = data.totals?.revenue;
    const expenses = data.totals?.expenses;

    return (
        <div className="co-card co-card-pad" data-testid="spend-insights-tile">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">
                        <Wallet className="h-3.5 w-3.5" /> Where your money went
                    </div>
                    <div className="mt-1 font-display text-lg font-medium text-[#111]">Last 90 days</div>
                    <div className="mt-0.5 text-xs text-[#5E636E]">
                        You spent <strong className="text-[#111] tabular-nums">{NZD.format(expenses || 0)}</strong>
                        {revenue != null && <> · brought in <strong className="text-[#111] tabular-nums">{NZD.format(revenue)}</strong></>}
                    </div>
                </div>
                <Link
                    to="/portal/cash-out"
                    className="inline-flex items-center gap-1 text-xs font-medium text-[#0E3B2E] hover:underline"
                    data-testid="spend-insights-tile-drilldown"
                >
                    See full breakdown <ArrowRight className="h-3.5 w-3.5" />
                </Link>
            </div>

            <ul className="mt-4 space-y-2" data-testid="spend-insights-tile-list">
                {rows.map((c) => {
                    const hue = c.__isOther ? 220 : hueFor(c.name);
                    return (
                        <li key={c.name} className="flex items-center gap-3">
                            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: `hsl(${hue} 55% 55%)` }} />
                            <span className="text-sm text-[#111] truncate flex-1">{c.name}</span>
                            <span className="text-xs tabular-nums text-[#5E636E] w-14 text-right">{c.share_pct}%</span>
                            <span className="text-sm font-medium tabular-nums text-[#111] w-24 text-right">{NZD.format(c.amount)}</span>
                            <ChangeBadge change={c.change_pct} flag={c.change_flag} />
                        </li>
                    );
                })}
            </ul>

            {data.updated_at && (
                <div className="mt-3 text-[10px] text-[#5E636E]">
                    Updated {new Date(data.updated_at).toLocaleDateString("en-NZ")} · {data.source === "webhook" ? "pushed by your accountant" : `via ${data.source}`}
                </div>
            )}
        </div>
    );
}

function ChangeBadge({ change, flag }) {
    if (change == null) return <span className="w-14" />;
    if (flag === "up_big") {
        return (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 text-[10px] font-medium w-fit">
                <AlertTriangle className="h-2.5 w-2.5" /> +{change}%
            </span>
        );
    }
    if (flag === "up") {
        return (
            <span className="inline-flex items-center gap-0.5 text-rose-700 text-[10px] tabular-nums w-14 justify-end">
                <TrendingUp className="h-3 w-3" /> +{change}%
            </span>
        );
    }
    if (flag === "down") {
        return (
            <span className="inline-flex items-center gap-0.5 text-emerald-700 text-[10px] tabular-nums w-14 justify-end">
                <TrendingDown className="h-3 w-3" /> {change}%
            </span>
        );
    }
    return <span className="w-14 text-right text-[10px] text-[#5E636E]">—</span>;
}
